import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { defaultRouteForUser, hasPageAuthority, pageKeyForPath } from '../accessControl';

const AuthRedirector = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;
    if (loading) return;

    if (!user) {
      if (currentPath !== '/login') {
        navigate('/login', { replace: true, state: { from: location } });
      }
      return;
    }

    const defaultRoute = defaultRouteForUser(user);

    if (user.mustChangePassword) {
      if (currentPath !== '/login') {
        navigate('/login', { replace: true });
      }
      return;
    }

    // If authed on /login, return to the requested page when allowed.
    if (currentPath === '/login') {
      const requestedPath = location.state?.from?.pathname;
      const requestedPageKey = requestedPath ? pageKeyForPath(requestedPath) : null;
      const canReturnToRequestedPath = requestedPath
        && requestedPath !== '/login'
        && (
          requestedPath === '/unauthorized'
          || !requestedPageKey
          || hasPageAuthority(user, requestedPageKey, 'read')
        );
      navigate(canReturnToRequestedPath ? requestedPath : defaultRoute, { replace: true });
      return;
    }

    // If authed and on root, send to default
    if (currentPath === '/') {
      navigate(defaultRoute, { replace: true });
      return;
    }

    const pageKey = pageKeyForPath(currentPath);
    const isAllowed = pageKey ? hasPageAuthority(user, pageKey, 'read') : currentPath === '/unauthorized';

    if (!isAllowed) {
      navigate(defaultRoute, { replace: true });
    }
  }, [user, loading, location, navigate]);

  return null;
};

export default AuthRedirector;
